<template>
  <div class="bg-blue-100 text-sm text-indigo-base">
    <div class="mx-5">
      <div class="flex flex-col items-baseline justify-between" :style="{ height: `${TABLEAU_PAGE_HEIGHT}px` }">
        <VizLoader
          v-if="shouldRenderViz"
          :key="selectedEntityId"
          :src="formsVizSource"
          :filters="filtersObj"
          :on-marks-selection="onMarksSelection"
          :token="tableauReportToken"
        >
          <template #body>
            <ReportModal
              v-if="isModalOpen"
              :title="modalTitle"
              :open="isModalOpen"
              :data="todoSubmissionData"
              @close-modal="closeModal"
            />
          </template>
        </VizLoader>

        <div v-else class="flex h-full w-full items-center justify-center">
          <div class="w-full h-screen relative" :class="isTokenLoading ? 'mt-8 overflow-hidden' : 'pt-0'">
            <Spinner v-if="isTokenLoading" class="h-screen" />
            <p v-else class="text-center text-sm text-indigo-base">{{ tableauReportErrorMessage }}</p>

              <ReportModal
                v-if="isModalOpen"
                :title="modalTitle"
                :open="isModalOpen"
                :data="todoSubmissionData"
                @close-modal="closeModal"
              />

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { validate as validateUUID } from "uuid";
import { computed, ref, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import { getTableauErrorMessage, TableauErrorCode } from "@/components/FormsApp/constants";
import Spinner from "@/components/helpers/Spinner.vue";
import VizLoader from "@/components/VizLoader/index.vue";
import ReportModal from "@/components/VizLoader/ReportModal.vue";
import { hasTodoViewSubmissionPermission, onboardingTodosReportV2FeatureFlag } from "@/composables/authorization.js";
import { formsVizSource } from "@/composables/tableau.js";
import { useExchangeMode } from "@/composables/useExchangeMode";
import { TABLEAU_PAGE_HEIGHT } from "@/constants.js";
import { useGenerateReportToken } from "@/providers/reports/useGenerateReportToken.ts";
import profileService from "@/services/profile.js";

defineOptions({
  name: "FormsApp",
});

const store = useStore();
const toast = useToast();
const todoSubmissionData = ref();
const modalTitle = ref();
const isModalOpen = ref(false);

// This is the token used to authenticate and generate the Tableau report.
const tableauReportToken = ref(undefined);
const tableauReportTokenError = ref(null);

const { exchangeMode } = useExchangeMode();

const { mutateAsync, isPending } = useGenerateReportToken();

const selectedEntityId = computed(() => {
  const entity = store.getters["programManager/getSelectedEntityView"];
  return entity?.id;
});

const shouldRenderViz = computed(() => {
  // Only check the tableauReportToken if the onboarding-todos-report-v2 flag is enabled.
  return onboardingTodosReportV2FeatureFlag.value ? Boolean(tableauReportToken.value) : true;
});
const isTokenLoading = computed(() => isPending.value && !tableauReportToken.value && !tableauReportTokenError.value);

const tableauReportErrorMessage = computed(() => getTableauErrorMessage(tableauReportTokenError.value));

const fetchReportToken = async () => {
  // Clear out the existing token and error states.
  tableauReportToken.value = undefined;
  tableauReportTokenError.value = undefined;

  if (selectedEntityId.value) {
    try {
      const { data } = await mutateAsync({
        entity_id: selectedEntityId.value,
        exchange_mode: exchangeMode.value,
      });

      if (data?.token) {
        tableauReportToken.value = data.token;
      } else {
        tableauReportTokenError.value = TableauErrorCode.TokenNotFound;
      }
    } catch (error) {
      const errorCode = TableauErrorCode.UnexpectedError;
      tableauReportTokenError.value = errorCode;

      toast.open({
        message: getTableauErrorMessage(errorCode),
        type: "error",
        position: "bottom",
      });
    }
  } else {
    tableauReportTokenError.value = TableauErrorCode.EntityNotFound;
  }
};

watch(
  [selectedEntityId, exchangeMode],
  ([newSelectedEntityId, newExchangeMode], [oldSelectedEntityId, oldExchangeMode]) => {
    // Sanity check to prevent extra api calls.
    const isStaleToken = newSelectedEntityId !== oldSelectedEntityId || newExchangeMode !== oldExchangeMode;
    if (onboardingTodosReportV2FeatureFlag.value && isStaleToken) {
      // Only fetch the tableauToken if the onboarding-todos-report-v2 flag is enabled.
      fetchReportToken();
    }
  },
  { immediate: true },
);

const filtersObj = computed(() => {
  return [
    {
      name: "ENTITY_ID",
      value: selectedEntityId.value,
    },
  ];
});

const closeModal = () => {
  isModalOpen.value = false;
};

const downloadFromS3 = async ({ url, fileName }) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

const downloadSubmissionFile = async ({ bucket, fileKey, submissionId }) => {
  if (bucket && fileKey) {
    try {
      const response = await profileService.getDocumentPreSignedUrl({
        bucket_name: bucket,
        object_name: fileKey,
        expiration: 60,
        submission_id: submissionId,
      });
      const url = response?.data?.data?.url;
      if (url) {
        await downloadFromS3({ url, fileName: fileKey });
      } else {
        return notifyUserHasNoSubmissionData();
      }
    } catch (error) {
      toast.open({
        message: "Error Trying to download the file. Please, try it later.",
        type: "error",
        position: "bottom",
      });
    }
  }
};

// clean up some data from json to show for user
const cleanUpSubmissionData = (submissionData) => {
  const data = structuredClone(submissionData);
  delete data?.application_id;
  delete data?.request_sent;
  delete data?.document_upload;
  delete data?.resume_upload;
  for (let idx = 0; idx < data?.passports?.length || 0; idx++) {
    delete data?.passports?.[idx]?.image_upload;
  }
  return data;
};

const getSubmission = async (submissionId) => {
  if (submissionId) {
    const response = await profileService.getSubmissionsById(submissionId);
    return response?.data?.data;
  }
};

const notifyUserHasNoSubmissionData = () => {
  toast.open({
    message:
      "We’ve received the submission, but the file isn’t available for download at this time. If you need a copy, please reach out to us at productsupport@apiexperience.com.",
    type: "info",
    position: "bottom",
  });
};

const getSubmissionData = async ({ bucket, fileKey, submissionId }) => {
  if (bucket && fileKey) {
    return await downloadSubmissionFile({ bucket, fileKey, submissionId });
  }
  if (!submissionId) {
    return notifyUserHasNoSubmissionData();
  }
  const submission = await getSubmission(submissionId);
  const submissionData = submission?.submission_data;
  const documents =
    submissionData?.document_upload ??
    submissionData?.passports?.map((item) => item?.image_upload?.[0]) ??
    submissionData?.resume_upload ??
    [];
  if (documents.length > 0) {
    for (const document of documents) {
      const bucket = document.bucket;
      const fileKey = document.key;
      if (!bucket && !fileKey) return notifyUserHasNoSubmissionData();
      await downloadSubmissionFile({ bucket, fileKey, submissionId });
    }
  } else {
    if (Object.keys(cleanUpSubmissionData(submissionData))?.length === 0) {
      return notifyUserHasNoSubmissionData();
    }
    // clean up the data and show it in the modal
    todoSubmissionData.value = {
      ...cleanUpSubmissionData(submissionData),
    };
    isModalOpen.value = true;
  }
};

// Tableau report null fields like this "%null%", so just setting a common null value to keep logic working
const handleReportNullValues = (value) => {
  if (!value || value === "%null%") return null;
  return value;
};

const getColumnIndex = (columns, field) => {
  return columns.find((each) => each["_fieldName"] === field)?.["_index"];
};

// This event is triggered by some field marked in report ui, not only submission clicks,
// so we need to map fields an indexes to make sure we are handling expected click action
const onMarksSelection = async (event) => {
  // mapped columns to be able to grab the index of each and then the values
  const mappedColumnNames = {
    formName: "Form Name",
    firstName: "First Name",
    lastName: "Last Name",
    orderId: "Order Id",
    bucketName: "bucket",
    submissionId: "submission_id",
    fileKey: "key",
  };
  const marksSelected = await event.detail.getMarksAsync();
  const marksData = marksSelected.data[0].data;
  // Discarting clicks not in view form area
  if (marksSelected?.data[0]?.totalRowCount > 1) return;
  const isViewSubmissionClick = Object.values(mappedColumnNames).every((field) =>
    marksSelected.data[0].columns.map((each) => each["_fieldName"]).includes(field),
  );
  // If we have all mapped fields there, it should be the view click we expected
  if (isViewSubmissionClick) {
    if (!hasTodoViewSubmissionPermission.value) {
      return toast.open({
        message: "You do not have access to view this student's data. Please contact your system administrator.",
        type: "error",
        position: "bottom",
      });
    }
    const columns = marksSelected.data[0].columns;
    // mapping the indexes
    const submissionIdIndex = getColumnIndex(columns, mappedColumnNames.submissionId);
    const bucketIndex = getColumnIndex(columns, mappedColumnNames.bucketName);
    const fileKeyIndex = getColumnIndex(columns, mappedColumnNames.fileKey);
    const formNameIndex = getColumnIndex(columns, mappedColumnNames.formName);
    const firstNameIndex = getColumnIndex(columns, mappedColumnNames.firstName);
    const lastNameIndex = getColumnIndex(columns, mappedColumnNames.lastName);
    const firstItemFromMarksData = marksData[0];
    const submissionId = validateUUID(firstItemFromMarksData[submissionIdIndex]["_value"])
      ? handleReportNullValues(firstItemFromMarksData[submissionIdIndex]["_value"])
      : null;
    const formName = handleReportNullValues(firstItemFromMarksData[formNameIndex]["_value"]);
    modalTitle.value = `${handleReportNullValues(
      firstItemFromMarksData[firstNameIndex]["_value"],
    )} ${handleReportNullValues(firstItemFromMarksData[lastNameIndex]["_value"])} - ${formName}`;
    const data = {
      formName: formName,
      bucket: handleReportNullValues(firstItemFromMarksData[bucketIndex]["_value"]),
      fileKey: handleReportNullValues(firstItemFromMarksData[fileKeyIndex]["_value"]),
      submissionId: submissionId,
    };
    await getSubmissionData(data);
  }
};
</script>
